import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';
import { crowdfundingFiscalSponsorshipContent } from '../content/educational/crowdfundingFiscalSponsorshipContent';

// Helper function to ensure text is never undefined
const safeText = (text) => text || '';

// Helper function to validate section content
const isValidSection = (section) => {
  if (!section || typeof section !== 'object') {
    console.error('Invalid section:', section);
    return false;
  }
  if (!section.heading || typeof section.heading !== 'string') {
    console.error('Invalid section heading:', section.heading);
    return false;
  }
  if (!section.text || typeof section.text !== 'string') {
    console.error('Invalid section text:', section.text);
    return false;
  }
  return true;
};

const generatePDF = async (content, title, keywords) => {
  try {
    // Validate inputs
    console.log('Generating PDF with:', { title, contentLength: content?.length, keywords });
    
    if (!content || !Array.isArray(content) || content.length === 0) {
      throw new Error('Content must be a non-empty array of sections');
    }
    
    if (!title || typeof title !== 'string') {
      throw new Error('Title must be a non-empty string');
    }
    
    if (!keywords || !Array.isArray(keywords)) {
      throw new Error('Keywords must be an array');
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 40;
    const contentWidth = pageWidth - (margin * 2);
    const headerHeight = 45;
    const footerHeight = 15;

    // Layout constants
    const bulletIndent = 10;
    const subIndent = 6;
    const lineSpacing = 8;
    const paragraphSpacing = 12;

    // Generate QR Codes
    const qrCodeHomepage = await QRCode.toDataURL('https://causeandimpact.org');
    const qrCodeApply = await QRCode.toDataURL('https://causeandimpact.org/apply');

    // Font sizes
    const fonts = {
      title: 24,
      sectionHeading: 16,
      subHeading: 12,
      body: 11,
      footer: 8,
      disclaimer: 9
    };

    // Colors
    const colors = {
      blue: [37, 99, 235],
      green: [5, 150, 105],
      gray: [31, 41, 55],
      white: [255, 255, 255],
      red: [220, 38, 38]  // For compliance warnings
    };

    // Add IRS compliance disclaimer
    const addComplianceDisclaimer = (yPosition) => {
      const disclaimerText = [
        "IMPORTANT: This guide provides general information about fiscal sponsorship for crowdfunding campaigns. ",
        "All campaigns must meet IRS requirements for charitable purpose under section 501(c)(3). ",
        "Individual fundraising and non-charitable campaigns are not eligible for fiscal sponsorship. ",
        "Consult with legal counsel regarding specific campaign compliance requirements."
      ].join('');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fonts.disclaimer);
      doc.setTextColor(...colors.red);
      
      const disclaimerLines = doc.splitTextToSize(disclaimerText, contentWidth);
      doc.text(disclaimerLines, margin, yPosition);
      
      return yPosition + (disclaimerLines.length * 5) + 8;
    };

    // Add compliance checklist
    const addComplianceChecklist = (yPosition) => {
      const checklistItems = [
        "• Campaign must serve charitable purposes as defined by IRS section 501(c)(3)",
        "• Funds must be used for approved program purposes only",
        "• No private benefit or inurement to individuals",
        "• Required documentation and reporting for all fund usage",
        "• Compliance with state charitable solicitation requirements",
        "• Proper donor acknowledgments and tax receipts"
      ];

      doc.setFontSize(fonts.body);
      doc.setTextColor(...colors.gray);
      
      // Add section heading for checklist
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fonts.sectionHeading);
      doc.setTextColor(...colors.green);
      doc.text("Compliance Requirements", margin, yPosition);
      yPosition += 20;

      // Reset font for checklist items
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fonts.body);
      doc.setTextColor(...colors.gray);
      
      // Calculate proper line height and spacing
      const lineHeight = fonts.body + 6; // Increased line height
      const bulletIndent = 6; // Space after bullet
      
      checklistItems.forEach(item => {
        // Check for page break with more space buffer
        if (yPosition + lineHeight > pageHeight - footerHeight - 30) {
          doc.addPage();
          addHeader();
          addFooter(doc.internal.getNumberOfPages());
          yPosition = headerHeight + 20;
        }
        
        // Split item into bullet and text
        const bullet = "•";
        const text = item.substring(1).trim();
        
        // Draw bullet
        doc.text(bullet, margin, yPosition);
        
        // Draw text with proper wrapping
        const textWidth = contentWidth - (margin / 2) - bulletIndent;
        const lines = doc.splitTextToSize(text, textWidth);
        
        lines.forEach((line, index) => {
          doc.text(line, margin + bulletIndent, yPosition + (index * (fonts.body * 0.8)));
        });
        
        yPosition += Math.max(lineHeight, lines.length * (fonts.body * 0.8)) + 4; // Added more spacing between items
      });

      return yPosition + 20; // More padding after checklist
    };

    // Update addCallToAction function with more compact layout
    const addCallToAction = (yPosition) => {
      const ctaHeight = 45; // Reduced height
      const qrSize = 30; // Slightly smaller QR code
      const textPadding = 15; // Space for text area
      
      // Check if CTA will fit on current page
      if (yPosition + ctaHeight > pageHeight - footerHeight - 15) {
        return false;
      }
      
      // Draw blue background
      doc.setFillColor(...colors.blue);
      doc.roundedRect(margin, yPosition, contentWidth, ctaHeight, 6, 6, 'F');
      
      // Calculate text area width (leaving space for QR code)
      const textWidth = contentWidth - qrSize - 40; // Extra padding for QR code
      
      // Main CTA text
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16); // Smaller font
      doc.setTextColor(...colors.white);
      doc.text('Ready to Launch Your Compliant Campaign?', 
        margin + textPadding, 
        yPosition + 20, 
        { maxWidth: textWidth }
      );
      
      // Subtitle
      doc.setFontSize(11); // Smaller subtitle
      doc.setFont('helvetica', 'normal');
      doc.text('Scan to start your fiscal sponsorship application', 
        margin + textPadding, 
        yPosition + 35, 
        { maxWidth: textWidth }
      );
      
      // QR code positioning
      const qrX = pageWidth - qrSize - margin - 10;
      const qrY = yPosition + (ctaHeight - qrSize) / 2;
      
      // QR code shadow and background
      doc.setFillColor(0, 0, 0, 0.1);
      doc.roundedRect(qrX + 1, qrY + 1, qrSize, qrSize, 3, 3, 'F');
      
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(qrX, qrY, qrSize, qrSize, 3, 3, 'F');
      
      doc.addImage(qrCodeApply, 'PNG', qrX, qrY, qrSize, qrSize);
      
      return true;
    };

    // Helper function to process markdown text
    const processMarkdown = (text) => {
      if (!text) return [{ text: '', bold: false }];
      
      let segments = [];
      const boldRegex = /\*\*(.*?)\*\*/g;
      let lastIndex = 0;
      let match;
      
      try {
        while ((match = boldRegex.exec(text)) !== null) {
          if (match.index > lastIndex) {
            segments.push({
              text: text.substring(lastIndex, match.index),
              bold: false
            });
          }
          segments.push({
            text: match[1],
            bold: true
          });
          lastIndex = match.index + match[0].length;
        }
        
        if (lastIndex < text.length) {
          segments.push({
            text: text.substring(lastIndex),
            bold: false
          });
        }
      } catch (error) {
        console.error('Error processing markdown:', error);
        return [{ text: safeText(text), bold: false }];
      }
      
      return segments.length > 0 ? segments : [{ text: safeText(text), bold: false }];
    };

    // Helper function to handle bold keywords
    const processKeywords = (text, keywords) => {
      let processedText = text;
      keywords.forEach(keyword => {
        const regex = new RegExp(`(${keyword})`, 'gi');
        processedText = processedText.replace(regex, '**$1**');
      });
      return processedText;
    };

    // Helper function to handle bullet points and numbered lists
    const processList = (text, yPosition) => {
      const lines = text.split('\n');
      let currentY = yPosition;
      const numberWidth = 8;
      
      const renderTextWithBold = (text, x, y) => {
        const segments = processMarkdown(text);
        let currentX = x;
        
        segments.forEach(segment => {
          doc.setFont('helvetica', segment.bold ? 'bold' : 'normal');
          doc.text(segment.text, currentX, y);
          currentX += doc.getTextWidth(segment.text);
        });
        doc.setFont('helvetica', 'normal');
      };

      lines.forEach(line => {
        if (line.trim().startsWith('- ')) {
          const bulletText = line.substring(2);
          doc.text('•', margin + bulletIndent, currentY);
          const processedBullet = processKeywords(bulletText, keywords);
          const bulletLines = doc.splitTextToSize(processedBullet, contentWidth - (bulletIndent + 8));
          
          bulletLines.forEach((bLine) => {
            if (currentY + 4 > pageHeight - footerHeight) {
              doc.addPage();
              addHeader();
              addFooter(doc.internal.getNumberOfPages());
              currentY = headerHeight + 12;
            }
            renderTextWithBold(bLine, margin + bulletIndent + 6, currentY);
            currentY += 4;
          });
          currentY += 1;
        } else if (line.match(/^\d+\./)) {
          const numberPart = line.substring(0, line.indexOf('.') + 1);
          const textPart = line.substring(line.indexOf('.') + 1).trim();
          const processedNumber = processKeywords(textPart, keywords);
          const numberLines = doc.splitTextToSize(processedNumber, contentWidth - (bulletIndent + numberWidth + 8));
          
          numberLines.forEach((nLine, index) => {
            if (index === 0) {
              doc.text(numberPart, margin + bulletIndent, currentY);
              renderTextWithBold(nLine, margin + bulletIndent + numberWidth + 2, currentY);
            } else {
              renderTextWithBold(nLine, margin + bulletIndent + numberWidth + 2, currentY);
            }
            currentY += 4;
          });
          currentY += 1;
        } else {
          const processedLine = processKeywords(line, keywords);
          const textLines = doc.splitTextToSize(processedLine, contentWidth);
          
          textLines.forEach((tLine) => {
            renderTextWithBold(tLine, margin, currentY);
            currentY += 4;
          });
          currentY += 1;
        }
      });
      
      return currentY;
    };

    const addHeader = () => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, 0, pageWidth, headerHeight, 'F');

      // Gradient title effect
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      
      // Blue shadow
      doc.setTextColor(37, 99, 235);
      doc.text('Cause & Impact, Inc.', (pageWidth / 2) + 0.5, 20.1, { align: 'center' });
      
      // Green overlay
      doc.setTextColor(5, 150, 105);
      doc.text('Cause & Impact, Inc.', pageWidth / 2, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text('Starting a Nonprofit Cause never got easier!', pageWidth / 2, 30, { align: 'center' });

      doc.addImage(qrCodeHomepage, 'PNG', pageWidth - margin - 25, 10, 20, 20);
    };

    const addFooter = (pageNumber) => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.text(
        'Cause & Impact, Inc. | EIN: 84-3918881 | www.causeandimpact.org',
        pageWidth / 2,
        pageHeight - 8,
        { align: 'center' }
      );
      doc.text(`Page ${pageNumber}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
    };

    // Update the content addition logic in addContent
    const addContent = () => {
      let yPosition = headerHeight + 25; // Increase initial spacing
      let pageNumber = 1;

      // Title rendering with better spacing and single line
      doc.setFontSize(fonts.title);
      doc.setTextColor(...colors.blue);
      doc.setFont('helvetica', 'bold');
      
      // Calculate optimal width for title
      const titleWidth = pageWidth - (margin * 3); // Increased margin for title
      
      // Split title into main parts
      const titleParts = [
        "Crowdfunding Fiscal Sponsorship:",
        "Unlock Tax-Deductible Giving for Campaigns"
      ];
      
      // Render each part of the title
      titleParts.forEach((part, index) => {
        doc.text(part, pageWidth / 2, yPosition + (index * fonts.title), { align: 'center' });
      });
      
      yPosition += (titleParts.length * fonts.title) + 15; // Adjust spacing after title

      // Add compliance disclaimer with better spacing
      yPosition = addComplianceDisclaimer(yPosition);
      yPosition += 15;

      // Process sections with validation
      content.forEach((section, index) => {
        if (!isValidSection(section)) {
          console.error(`Skipping invalid section at index ${index}`);
          return;
        }

        if (yPosition + 40 > pageHeight - footerHeight - 15) {
          doc.addPage();
          pageNumber++;
          addHeader();
          addFooter(pageNumber);
          yPosition = headerHeight + 10;
        }

        // Section Heading
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(fonts.sectionHeading);
        doc.setTextColor(...colors.green);
        doc.text(safeText(section.heading), margin, yPosition);
        yPosition += 10;

        // Section content
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(fonts.body);
        doc.setTextColor(...colors.gray);

        const paragraphs = safeText(section.text).split('\n\n');
        paragraphs.forEach(paragraph => {
          if (yPosition + 40 > pageHeight - footerHeight - 15) {
            doc.addPage();
            pageNumber++;
            addHeader();
            addFooter(pageNumber);
            yPosition = headerHeight + 10;
          }

          yPosition = processList(paragraph, yPosition);
          yPosition += 4;
        });

        yPosition += 12;
      });

      // Add compliance checklist
      yPosition = addComplianceChecklist(yPosition);
      yPosition += 15; // Reduced spacing before CTA

      // Try to add CTA on current page
      if (!addCallToAction(yPosition)) {
        // If CTA doesn't fit, try to fit it on the previous page
        const lastSection = content[content.length - 1];
        if (lastSection) {
          // Recalculate position to fit CTA on previous page
          yPosition -= 60;
          addCallToAction(yPosition);
        }
      }
    };

    // Generate PDF with error handling
    try {
      addHeader();
      addFooter(1);
      addContent();
      return doc;
    } catch (error) {
      console.error('Error during PDF generation:', error);
      throw error;
    }

  } catch (error) {
    console.error('Error in generatePDF:', error);
    return null;
  }
};

export const generateCrowdfundingFiscalSponsorshipOverview = async () => {
  try {
    console.log('Starting crowdfunding PDF generation');
    
    // Validate content structure
    if (!crowdfundingFiscalSponsorshipContent) {
      throw new Error('Content is missing');
    }

    const { sections, title, keywords } = crowdfundingFiscalSponsorshipContent;
    
    if (!sections || !Array.isArray(sections) || sections.length === 0) {
      throw new Error('Invalid content structure: sections missing or empty');
    }

    // Generate PDF
    console.log('Generating PDF with content');
    const doc = await generatePDF(sections, title, keywords);
    
    if (!doc) {
      throw new Error('PDF generation failed - null document returned');
    }

    console.log('PDF generated successfully');
    return doc;
  } catch (error) {
    console.error('Error in generateCrowdfundingFiscalSponsorshipOverview:', error);
    throw error;
  }
}; 